// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Banner.tsx
 * @input Uses React useState, Button, Icon (with registry string names), StyleX
 * @output Exports Banner component, BannerProps, BannerStatus, BannerContainer types
 * @position Core implementation; consumed by index.ts, tested by Banner.test.tsx
 *
 * Visual structure:
 * - Root container: layout-only wrapper (flex column), no visual styling, no theme target
 * - Header area (themeProps 'banner'): colored status background with icon, title, description, actions, dismiss
 * - Content area (themeProps 'banner-content'): collapsible card background for additional content (children)
 * - No left border accent — color is expressed through the full header background
 * - Each visual area owns its own border-radius (no overflow:clip on the container)
 * - When children are provided, a collapse/expand toggle button appears in the end area
 *
 * Title and description render as <div> (not <p>): they accept arbitrary
 * ReactNode content, and <p> cannot legally contain block-level children
 * (the HTML parser reparents them, desyncing SSR markup from the hydrated
 * DOM). Using <div> keeps these slots composable with any content. Their
 * StyleX styles set margin: 0 and explicit typography, so the rendered
 * appearance is identical to the previous <p>.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Banner/Banner.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Banner/Banner.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Banner/index.ts (exports if types change)
 * - /apps/storybook/stories/Banner.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Banner/ (showcase blocks)
 */
import { useId, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, fontWeightVars, typeScaleVars, borderVars, durationVars, easeVars, shadowVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { edgeCompSlot } from "../Layout/edgeCompensation.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================

/**
 * Status type controlling the banner's icon and color.
 * Extensible via module augmentation of BannerStatusMap.
 */

/**
 * Container type of the banner.
 * - `card`: standalone card with border-radius and shadow
 * - `section`: full-width section banner (no border-radius)
 *
 * Extensible via module augmentation of BannerContainerMap.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Status → Icon mapping
// =============================================================================

const defaultIconNames = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success'
};

// =============================================================================
// Status → ARIA role mapping
// =============================================================================

const statusRole = {
  info: 'status',
  warning: 'alert',
  error: 'alert',
  success: 'status'
};

// =============================================================================
// Status → Icon color mapping
// =============================================================================

const statusIconColor = {
  info: 'accent',
  warning: 'warning',
  error: 'error',
  success: 'success'
};

// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kMv6JI: "xjb2p0i",
    $$css: true
  },
  rootElevatedCard: {
    kaIpWk: "x1hviunn",
    $$css: true
  },
  header: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1txdalj",
    k8WAf4: "x8o8v82",
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  headerCardStandalone: {
    kaIpWk: "x1hviunn",
    $$css: true
  },
  headerCardWithContent: {
    krdFHd: "x81l70g",
    kfmiAY: "x7hs6f1",
    kVL7Gh: "xbiv7yw",
    kT0f0o: "x16uus16",
    $$css: true
  },
  headerCentered: {
    kGNEyG: "x6s0dn4",
    $$css: true
  },
  endArea: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kmuXW: "x2lah0s",
    keTefX: "xvc5jky",
    kqGvvJ: "x81ka23",
    $$css: true
  }
};
const statusStyles = {
  info: {
    kWkggS: "xgcxg3y",
    $$css: true
  },
  warning: {
    kWkggS: "x24i8r5",
    $$css: true
  },
  error: {
    kWkggS: "x1pritpl",
    $$css: true
  },
  success: {
    kWkggS: "xu13z74",
    $$css: true
  }
};

// Resting elevation for the banner. Applied to the root so the shadow wraps
// the whole banner (header + optional content). 'none' is the default and
// leaves the layout-only root untouched. For the `card` container the root is
// rounded (rootElevatedCard) so the shadow follows the card silhouette; the
// full-width `section` container stays square.
const elevationStyles = {
  none: {
    kGVxlE: "x1gnnqk1",
    $$css: true
  },
  low: {
    kGVxlE: "x1i5ehqx",
    $$css: true
  },
  med: {
    kGVxlE: "x14hfi27",
    $$css: true
  },
  high: {
    kGVxlE: "x1kcpxr7",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A persistent status notification banner for info, warning, error, or success messages.
 *
 * Two-part visual structure:
 * - Header: colored status background with icon, title, description, and actions
 * - Content (optional): collapsible card background area for additional rich content
 *
 * When children are provided, a collapse/expand chevron button appears in the
 * header end area (to the left of the dismiss button if present). Clicking it
 * toggles the visibility of the content area.
 *
 * Manages its own dismissed state internally — the banner hides on dismiss
 * even if `onDismiss` is not provided, so product teams don't need to wire
 * up state management for basic dismiss behavior.
 *
 * Uses `role="alert"` for error/warning and `role="status"` for info/success.
 *
 * @example
 * ```
 * <Banner status="info" title="New update available" />
 * <Banner
 *   status="error"
 *   title="Something went wrong"
 *   description="Please try again later."
 *   isDismissable
 *   onDismiss={() => logDismiss()}
 * />
 * <Banner
 *   status="error"
 *   title="Multiple errors found"
 *   description="The following issues need to be resolved:"
 *   isDismissable>
 *   <ul>
 *     <li>Email address is invalid</li>
 *     <li>Password must be at least 8 characters</li>
 *   </ul>
 * </Banner>
 * <Banner
 *   status="warning"
 *   title="Configuration changes"
 *   defaultIsExpanded>
 *   <p>Details here...</p>
 * </Banner>
 * ```
 */
export function Banner({
  status,
  title,
  description,
  icon,
  isDismissable = false,
  onDismiss,
  endContent,
  container = 'card',
  elevation = 'none',
  defaultIsExpanded = false,
  children,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);
  // Links the expand/collapse toggle to the content region it shows/hides so
  // assistive tech can move from the button to its controlled content
  // (disclosure pattern). The region is conditionally rendered, so aria-controls
  // below is set only while it's mounted to avoid a dangling reference.
  const contentId = useId();
  const defaultIconName = defaultIconNames[status];
  const role = statusRole[status];
  const iconColor = statusIconColor[status];
  const hasChildren = children != null;
  if (isDismissed) {
    return null;
  }
  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };
  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  // Show the end area if there are actions, dismiss, or a collapsible toggle
  const showEndArea = endContent != null || isDismissable || hasChildren;
  // Center items vertically when there's only a title (no description)
  // and the banner has action buttons
  const hasActions = endContent != null || isDismissable;
  const isSingleLine = description == null && hasActions;
  const showContent = hasChildren && isExpanded;
  const isCard = container === 'card';
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    role: role,
    ...mergeProps(stylex.props(styles.root, elevationStyles[elevation], isCard && elevation !== 'none' && styles.rootElevatedCard, xstyle), className, style),
    ...rest,
    children: [/*#__PURE__*/_jsxs("div", {
      ...mergeProps(themeProps('banner', {
        container,
        status
      }), stylex.props(styles.header, isSingleLine && styles.headerCentered, statusStyles[status], isCard && (showContent ? styles.headerCardWithContent : styles.headerCardStandalone))),
      children: [/*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('banner-icon', {
          status
        }), {
          className: "x78zum5 x6s0dn4 x2lah0s"
        }),
        "aria-hidden": "true",
        children: icon != null ? icon : /*#__PURE__*/_jsx(Icon, {
          icon: defaultIconName,
          size: "md",
          color: iconColor
        })
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf xxhr3t x98rzlu xeuugli"
        },
        children: [/*#__PURE__*/_jsx("div", {
          ...{
            className: "x1ghz6dp xjb2p0i xcr08ib x2mo6ok x1kq96og x1tgivj0"
          },
          children: title
        }), description != null && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x1ghz6dp xjb2p0i x141an7d x1sodnla x1ltkj2j xv1l7n4"
          },
          children: description
        })]
      }), showEndArea && /*#__PURE__*/_jsxs("div", {
        ...stylex.props(styles.endArea, edgeCompSlot.inset(spacingVars['--spacing-2'])),
        children: [endContent, hasChildren && /*#__PURE__*/_jsx(Button, {
          variant: "ghost",
          size: "sm",
          label: isExpanded ? t('@astryx.banner.collapse') : t('@astryx.banner.expand'),
          tooltip: isExpanded ? t('@astryx.banner.collapse') : t('@astryx.banner.expand'),
          icon: /*#__PURE__*/_jsx("span", {
            ...{
              0: {
                className: "x3nfvp2 x11xpdln xuedmi6 x12w9bfk xlr8y92"
              },
              1: {
                className: "x3nfvp2 x11xpdln xuedmi6 x12w9bfk xlr8y92 x19jd1h0"
              }
            }[!!isExpanded << 0],
            children: /*#__PURE__*/_jsx(Icon, {
              icon: "chevronDown",
              size: "sm",
              color: "inherit"
            })
          }),
          onClick: handleToggleExpand,
          "aria-expanded": isExpanded,
          "aria-controls": showContent ? contentId : undefined,
          isIconOnly: true
        }), isDismissable && /*#__PURE__*/_jsx(Button, {
          variant: "ghost",
          size: "sm",
          label: t('@astryx.banner.dismiss'),
          tooltip: t('@astryx.banner.dismiss'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            size: "sm",
            color: "inherit"
          }),
          onClick: handleDismiss,
          isIconOnly: true
        })]
      })]
    }), showContent && /*#__PURE__*/_jsx("div", {
      id: contentId,
      ...mergeProps(themeProps('banner-content', {
        container,
        status
      }), {
        0: {
          className: "x1de1mus x8o8v82 x1pzlopt xgbv0en xw8tdv1 x92x3c3 x1t7ytsu x18b5jzi x1q0q8m5 x1j92z86 x1gejf6u xw8gpjh"
        },
        1: {
          className: "x1de1mus x8o8v82 x1pzlopt xgbv0en xw8tdv1 x92x3c3 x1t7ytsu x18b5jzi x1q0q8m5 x1j92z86 x1gejf6u xw8gpjh x14k8p9y xiaxfje"
        }
      }[!!isCard << 0]),
      children: children
    })]
  });
}
Banner.displayName = 'Banner';